// utils/printDocument.ts - Complete file with direct API fetch

import QRCode from 'qrcode';
import { getEInvoicePublicVerifyUrl } from '@/utils/gstCalculation';

const API_URL = import.meta.env.VITE_API_URL;

export interface PrintLineItem {
  sr: number;
  name: string;
  hsn: string;
  qty: number;
  unit: string;
  rate: number;
  discount: number;
  taxable: number;
  taxRate: number;
  taxAmt: number;
  total: number;
  cgst?: number;
  sgst?: number;
  igst?: number;
  batchNumber?: string;
  mfgDate?: string;
  expiryDate?: string;
  serials?: any[];
  serialNumbers?: string[] | string;
}

export interface PrintInvoiceData {
  invoiceNo: string;
  date: string;
  customerName: string;
  customerGstin?: string;
  warehouseName?: string;
  warehouseAddress?: string;
  warehouseGstin?: string;
  billingAddress: string;
  shippingAddress?: string;
  paymentMode: string;
  paymentStatus?: string;
  paidAmount?: number;
  balanceDue?: number;
  showPaymentTable?: boolean;
  paymentModes?: Array<{ mode: string; amount: number; status?: string }>;
  remainingDue?: number;
  items: PrintLineItem[];
  subtotal: number;
  totalDiscount: number;
  taxableAmount: number;
  cgst: number;
  sgst: number;
  igst: number;
  roundOff: number;
  grandTotal: number;
  isSameState: boolean;
  notes?: string;
  irn?: string;
  ackNumber?: string;
  ackDate?: string;
  qrCodeData?: string;
  // TDS reference note — informational only, never subtracted from grandTotal above.
  tdsApplicable?: boolean;
  tdsSection?: string;
  tdsRate?: number;
  tdsAmount?: number;
  expectedReceipt?: number;
  // TCS note — unlike TDS this amount IS already included in grandTotal above
  // (real money collected from the customer under Section 206C(1H)).
  tcsApplicable?: boolean;
  tcsRate?: number;
  tcsAmount?: number;
}

export interface PrintChallanData {
  challanNo: string;
  date: string;
  customerName: string;
  billingAddress: string;
  shippingAddress?: string;
  vehicleNo?: string;
  driverName?: string;
  lrNo?: string;
  warehouseName?: string;
  warehouseAddress?: string;
  warehouseGstin?: string;
  items: Array<{
    sr: number;
    name: string;
    qty: number;
    unit: string;
    rate?: number;
    amount?: number;
    batchNumber?: string;
    serialNumbers?: string[] | string;
    serials?: any[];
  }>;
}

export interface PrintPOData {
  poNo: string;
  date: string;
  deliveryDate?: string;
  paymentTerms?: string;
  supplierName: string;
  supplierGstin?: string;
  supplierAddress: string;
  items: Array<{
    sr: number;
    name: string;
    hsn: string;
    qty: number;
    unit: string;
    rate: number;
    amount: number;
  }>;
  totalAmount: number;
  notes?: string;
}

function formatINR(n: number): string {
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function amountInWords(amount: number): string {
  const units = [
    '',
    'One',
    'Two',
    'Three',
    'Four',
    'Five',
    'Six',
    'Seven',
    'Eight',
    'Nine',
    'Ten',
    'Eleven',
    'Twelve',
    'Thirteen',
    'Fourteen',
    'Fifteen',
    'Sixteen',
    'Seventeen',
    'Eighteen',
    'Nineteen',
  ];
  const tens = [
    '',
    '',
    'Twenty',
    'Thirty',
    'Forty',
    'Fifty',
    'Sixty',
    'Seventy',
    'Eighty',
    'Ninety',
  ];

  function toWords(n: number): string {
    if (n === 0) return '';
    if (n < 20) return units[n];
    if (n < 100)
      return (
        tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + units[n % 10] : '')
      );
    if (n < 1000)
      return (
        units[Math.floor(n / 100)] +
        ' Hundred' +
        (n % 100 !== 0 ? ' ' + toWords(n % 100) : '')
      );
    if (n < 100000)
      return (
        toWords(Math.floor(n / 1000)) +
        ' Thousand' +
        (n % 1000 !== 0 ? ' ' + toWords(n % 1000) : '')
      );
    if (n < 10000000)
      return (
        toWords(Math.floor(n / 100000)) +
        ' Lakh' +
        (n % 100000 !== 0 ? ' ' + toWords(n % 100000) : '')
      );
    return (
      toWords(Math.floor(n / 10000000)) +
      ' Crore' +
      (n % 10000000 !== 0 ? ' ' + toWords(n % 10000000) : '')
    );
  }

  const rupees = Math.floor(amount);
  const paise = Math.round((amount - rupees) * 100);
  let words = 'Rupees ' + (toWords(rupees) || 'Zero');
  if (paise > 0) words += ' and ' + toWords(paise) + ' Paise';
  return words + ' Only';
}

function commonStyles(): string {
  return `
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; font-size: 11px; color: #1e293b; padding: 10mm; }
    table { width: 100%; border-collapse: collapse; }
    th { background: #f8fafc; font-weight: 600; text-align: left; padding: 6px 8px; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; color: #64748b; }
    td { padding: 5px 8px; border-bottom: 1px solid #f1f5f9; }
    .border-table th, .border-table td { border: 1px solid #e2e8f0; }
    .right { text-align: right; }
    .center { text-align: center; }
    .bold { font-weight: 700; }
    .section { margin-bottom: 12px; }
    .label { font-size: 9px; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.5px; }
    .company-name { font-size: 16px; font-weight: 700; color: #1e293b; }
    .doc-title { font-size: 18px; font-weight: 800; color: #4f46e5; text-align: right; }
    .header-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 14px; padding-bottom: 10px; border-bottom: 2px solid #e2e8f0; }
    .address-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 12px; padding: 10px; background: #f8fafc; border-radius: 4px; }
    .totals-box { float: right; width: 280px; }
    .totals-row { display: flex; justify-content: space-between; padding: 4px 8px; }
    .totals-row.grand { background: #4f46e5; color: white; font-weight: 700; font-size: 13px; border-radius: 4px; padding: 8px; margin-top: 4px; }
    .tax-table { margin-top: 12px; clear: both; }
    .amount-words { margin-top: 12px; padding: 8px 12px; background: #f8fafc; border-left: 3px solid #4f46e5; font-style: italic; }
    .bank-details { margin-top: 10px; padding: 8px 12px; background: #f8fafc; border-radius: 4px; }
    .signature { text-align: right; margin-top: 40px; border-top: 1px solid #e2e8f0; padding-top: 8px; font-size: 10px; color: #64748b; }
    .footer-text { text-align: center; font-style: italic; color: #94a3b8; font-size: 9px; margin-top: 16px; padding-top: 8px; border-top: 1px solid #f1f5f9; }
    @media print { @page { size: A4; margin: 0; } body { padding: 10mm 12mm; } }
  `;
}

// ✅ Company cache - ek baar fetch karo, baar baar use karo
let companyCache: any = null;
let companyFetchPromise: Promise<any> | null = null;

export async function fetchCompanyDetails(): Promise<any> {
  if (companyCache) return companyCache;

  if (companyFetchPromise) {
    return companyFetchPromise;
  }

  companyFetchPromise = (async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/v1/company/get`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      const result = await response.json();
      if (result?.success && result?.data) {
        companyCache = result.data;
        console.log('✅ Company fetched:', companyCache);
        return companyCache;
      }
      console.warn('⚠️ No company data, using defaults');
      return null;
    } catch (error) {
      console.error('❌ Failed to fetch company:', error);
      return null;
    } finally {
      companyFetchPromise = null;
    }
  })();

  return companyFetchPromise;
}

// ✅ Helper function to get company - API se fetch karo
async function getCompany(): Promise<any> {
  const companyData = await fetchCompanyDetails();

  // Default fallback values
  const defaultCompany = {
    name: 'Company Name',
    address: '',
    gstin: '',
    pan: '',
    phone: '',
    email: '',
    logo_url: null,
    // Bank details come from Settings → Bank Accounts (primary account); never invented
    bank_name: '',
    bank_account: '',
    bank_ifsc: '',
  };

  if (!companyData) {
    return defaultCompany;
  }

  return {
    name: companyData.name || defaultCompany.name,
    address: companyData.address || defaultCompany.address,
    gstin: companyData.gstin || defaultCompany.gstin,
    pan: companyData.pan || defaultCompany.pan,
    phone: companyData.phone || defaultCompany.phone,
    email: companyData.email || defaultCompany.email,
    logo_url: companyData.logo_url || defaultCompany.logo_url,
    bank_name: companyData.bank_name || defaultCompany.bank_name,
    bank_account: companyData.bank_account || defaultCompany.bank_account,
    bank_ifsc: companyData.bank_ifsc || defaultCompany.bank_ifsc,
  };
}

export async function printSalesInvoice(data: PrintInvoiceData): Promise<void> {
  const c = await getCompany();

  const taxSlabs = [5, 12, 18, 28]
    .map((rate) => {
      const slabItems = data.items.filter((i) => i.taxRate === rate);
      const taxable = slabItems.reduce((s, i) => s + i.taxable, 0);
      const cgst = (taxable * rate) / 200;
      const sgst = (taxable * rate) / 200;
      const igst = (taxable * rate) / 100;
      return { rate, taxable, cgst, sgst, igst };
    })
    .filter((s) => s.taxable > 0);

  // ✅ Logo handling for print
  let logoHtml = '';
  if (c.logo_url) {
    const logoUrl = c.logo_url.startsWith('data:image')
      ? c.logo_url
      : `${API_URL}${c.logo_url}`;
    logoHtml = `<img src="${logoUrl}" alt="${c.name}" style="width:50px;height:50px;border-radius:50%;object-fit:cover;border:2px solid #4f46e5;background:#fff;margin-right:12px;" />`;
  } else {
    logoHtml = `<div style="width:50px;height:50px;border-radius:50%;background:#e0e7ff;display:flex;align-items:center;justify-content:center;font-size:20px;font-weight:bold;color:#4f46e5;border:2px solid #4f46e5;margin-right:12px;flex-shrink:0;">${c.name.charAt(0).toUpperCase()}</div>`;
  }

  const hasIRN = Boolean(data.irn);
  const verifyUrl = getEInvoicePublicVerifyUrl(data.irn || data.ackNumber || data.invoiceNo);

  let qrImageUrl = '';
  if (hasIRN) {
    try {
      qrImageUrl = await QRCode.toDataURL(verifyUrl, { margin: 1, width: 130, errorCorrectionLevel: 'M' });
    } catch {
      qrImageUrl = '';
    }
  }

  const html = `<!DOCTYPE html>
  <html>
  <head>
    <meta charset="utf-8">
    <title>Tax Invoice - ${data.invoiceNo}</title>
    <style>${commonStyles()}</style>
  </head>
  <body>
    <!-- Header with Logo & QR Code -->
    <div class="header-grid" style="display:flex;justify-content:space-between;align-items:flex-start;">
      <div style="display:flex;align-items:center;gap:12px;max-width:60%;">
        ${logoHtml}
        <div>
          <p class="company-name">${c.name}</p>
          <p style="color:#64748b;margin-top:4px;line-height:1.5">${c.address}</p>
          <p style="margin-top:4px">
            <strong>GSTIN:</strong> ${c.gstin} &nbsp;|&nbsp; 
            <strong>PAN:</strong> ${c.pan}
          </p>
          <p>
            <strong>Ph:</strong> ${c.phone} &nbsp;|&nbsp; 
            <strong>Email:</strong> ${c.email}
          </p>
        </div>
      </div>
      <div style="display:flex;align-items:center;gap:16px;text-align:right">
        ${hasIRN ? `
          <div style="text-align:center;border:1px solid #e2e8f0;padding:6px;border-radius:6px;background:#fff;">
            <img src="${qrImageUrl}" style="width:90px;height:90px;display:block;" alt="E-Invoice QR" />
            <span style="font-size:8px;font-weight:bold;color:#4f46e5;text-transform:uppercase;">E-Invoice QR</span>
          </div>
        ` : ''}
        <div style="text-align:right">
          <p class="doc-title">TAX INVOICE</p>
          <div style="margin-top:8px;display:inline-block;border:1px solid #e2e8f0;padding:6px 10px;border-radius:4px;text-align:left;font-size:11px;">
            <p class="label">Invoice No</p>
            <p class="bold">${data.invoiceNo}</p>
            <p class="label" style="margin-top:4px">Date</p>
            <p>${data.date}</p>
            <p class="label" style="margin-top:4px">Payment Mode</p>
            <p>${data.paymentMode}</p>
          </div>
        </div>
      </div>
    </div>

    ${hasIRN ? `
      <!-- E-Invoice IRN Details Banner -->
      <div style="margin:10px 0;padding:8px 12px;background:#f0fdf4;border:1px solid #bbf7d0;border-radius:6px;font-size:10px;color:#166534;font-family:monospace;word-break:break-all;">
        <div style="margin-bottom:2px;"><strong>IRN:</strong> ${data.irn}</div>
        <div><strong>Ack No:</strong> ${data.ackNumber || '—'} &nbsp;|&nbsp; <strong>Ack Date:</strong> ${data.ackDate || data.date} &nbsp;|&nbsp; <strong>Status:</strong> Active (IRP Verified)</div>
      </div>
    ` : ''}

    <!-- Dispatched From (Warehouse) / Bill To / Ship To (One Row) -->
    <div style="display:grid;grid-template-columns:${data.warehouseName ? '1fr 1fr 1fr' : '1fr 1fr'};gap:14px;margin-bottom:12px;padding:10px;background:#f8fafc;border-radius:4px;">
      ${data.warehouseName ? `
      <div>
        <p class="label" style="color:#4f46e5;font-weight:700;">Dispatched From (Warehouse)</p>
        <p class="bold" style="margin-top:3px;font-size:12px">${data.warehouseName}</p>
        ${data.warehouseGstin ? `<p style="margin-top:2px;font-family:monospace;font-size:10px;color:#4f46e5;">GSTIN: ${data.warehouseGstin}</p>` : ''}
        ${data.warehouseAddress ? `<p style="margin-top:2px;color:#64748b;line-height:1.4;">${data.warehouseAddress}</p>` : ''}
      </div>` : ''}
      <div>
        <p class="label">Bill To</p>
        <p class="bold" style="margin-top:3px;font-size:12px">${data.customerName}</p>
        ${data.customerGstin ? `<p style="margin-top:2px">GSTIN: ${data.customerGstin}</p>` : ''}
        <p style="margin-top:2px;color:#64748b;line-height:1.4">${data.billingAddress}</p>
      </div>
      <div>
        <p class="label">Ship To</p>
        <p class="bold" style="margin-top:3px;font-size:12px">${data.customerName}</p>
        <p style="margin-top:2px;color:#64748b;line-height:1.4">${data.shippingAddress || data.billingAddress}</p>
      </div>
    </div>

    <!-- Payment Details -->
    ${
      data.showPaymentTable !== false
        ? `
    <div style="margin-bottom:12px;border:1px solid #e2e8f0;border-radius:4px;overflow:hidden">
      <div style="padding:6px 10px;background:#f8fafc;border-bottom:1px solid #e2e8f0;font-size:10px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:0.5px">Payment Details</div>
      ${[
        ['Payment Mode', data.paymentMode || '—'],
        ['Payment Status', data.paymentStatus || '—'],
        ['Amount Paid', formatINR(data.paidAmount ?? 0)],
        ['Balance Due', formatINR(data.balanceDue ?? 0)],
      ]
        .map(
          ([label, value]) =>
            `<div style="display:flex;justify-content:space-between;padding:5px 10px;border-bottom:1px solid #f1f5f9">
          <span style="color:#64748b">${label}</span>
          <span style="font-weight:600">${value}</span>
        </div>`,
        )
        .join('')}
    </div>`
        : ''
    }

    <!-- Items Table -->
    <table class="border-table section">
      <thead>
        <tr>
          <th style="width:30px">Sr</th>
          <th>Description</th>
          <th>HSN</th>
          <th class="right">Qty</th>
          <th>Unit</th>
          <th class="right">Rate</th>
          <th class="right">Disc%</th>
          <th class="right">Taxable</th>
          <th class="right">GST%</th>
          <th class="right">Tax Amt</th>
          <th class="right">Total</th>
        </tr>
      </thead>
      <tbody>
        ${data.items
          .map((item, i) => {
            const rawSerials = (Array.isArray(item.serials) && item.serials.length > 0)
              ? item.serials
              : (Array.isArray(item.serialNumbers) && item.serialNumbers.length > 0)
              ? item.serialNumbers
              : (typeof item.serialNumbers === 'string' && item.serialNumbers.trim())
              ? item.serialNumbers
              : (Array.isArray((item as any).selectedSerials) && (item as any).selectedSerials.length > 0)
              ? (item as any).selectedSerials
              : item.serials || [];

            let rawList: any[] = [];
            if (Array.isArray(rawSerials)) {
              rawList = rawSerials;
            } else if (typeof rawSerials === 'string') {
              rawList = rawSerials.split(/[,\n]+/).map((s: string) => s.trim()).filter(Boolean);
            } else if (rawSerials) {
              rawList = [rawSerials];
            }

            const serialsList: string[] = rawList
              .map((s: any) => (typeof s === 'string' ? s : (s.serialNumber || s.serial_number || s.serialNo || s.id || '')))
              .filter(Boolean);

            const batchStr = item.batchNumber || (item as any).batch_number;

            return `
        <tr style="${i % 2 === 1 ? 'background:#fafafa' : ''}">
          <td class="center">${item.sr}</td>
          <td class="bold">
            <div>${item.name}</div>
            ${batchStr ? `<div style="font-size:9px;color:#065f46;font-family:monospace;font-weight:normal;margin-top:2px;">Batch: ${batchStr}${item.expiryDate ? ` · Exp: ${String(item.expiryDate).slice(0, 10)}` : ''}${item.mfgDate ? ` · Mfg: ${String(item.mfgDate).slice(0, 10)}` : ''}</div>` : ''}
            ${serialsList.length > 0 ? `<div style="font-size:9px;color:#4338ca;font-family:monospace;font-weight:normal;margin-top:2px;">S/N: ${serialsList.join(', ')}</div>` : ''}
          </td>
          <td>${item.hsn}</td>
          <td class="right">${item.qty}</td>
          <td>${item.unit}</td>
          <td class="right">${formatINR(item.rate)}</td>
          <td class="right">${item.discount > 0 ? item.discount + '%' : '—'}</td>
          <td class="right">${formatINR(item.taxable)}</td>
          <td class="right">${item.taxRate}%</td>
          <td class="right">${formatINR(item.taxAmt)}</td>
          <td class="right bold">${formatINR(item.total)}</td>
        </tr>`;
          })
          .join('')}
      </tbody>
    </table>

    <!-- Amount in Words, Bank Details, Totals -->
    <div style="display:grid;grid-template-columns:1fr 280px;gap:20px;margin-bottom:12px">
      <div>
        <div class="amount-words">
          <p class="label">Amount in Words</p>
          <p style="margin-top:4px;font-weight:600">${amountInWords(data.grandTotal)}</p>
        </div>
        ${c.bank_account ? `
        <div class="bank-details" style="margin-top:10px">
          <p class="label">Bank Details</p>
          <p style="margin-top:4px">
            <strong>Bank:</strong> ${c.bank_name} &nbsp;|&nbsp;
            <strong>A/C:</strong> ${c.bank_account}
          </p>
          ${c.bank_ifsc ? `<p><strong>IFSC:</strong> ${c.bank_ifsc}</p>` : ''}
        </div>` : ''}
      </div>
      <div>
        <div style="border:1px solid #e2e8f0;border-radius:4px;overflow:hidden">
          ${[
            ['Subtotal', formatINR(data.subtotal)],
            [
              'Total Discount',
              data.totalDiscount > 0
                ? `- ${formatINR(data.totalDiscount)}`
                : '—',
            ],
            ['Taxable Amount', formatINR(data.taxableAmount)],
            ...(data.isSameState
              ? [
                  ['CGST', formatINR(data.cgst)],
                  ['SGST', formatINR(data.sgst)],
                ]
              : [['IGST', formatINR(data.igst)]]),
            [
              'Round Off',
              data.roundOff !== 0
                ? data.roundOff > 0
                  ? `+ ${formatINR(data.roundOff)}`
                  : `- ${formatINR(Math.abs(data.roundOff))}`
                : '—',
            ],
          ]
            .map(
              ([label, value]) =>
                `<div style="display:flex;justify-content:space-between;padding:5px 10px;border-bottom:1px solid #f1f5f9">
              <span style="color:#64748b">${label}</span>
              <span>${value}</span>
            </div>`,
            )
            .join('')}
          <div style="display:flex;justify-content:space-between;padding:8px 10px;background:#4f46e5;color:white;font-weight:700;font-size:13px">
            <span>Grand Total</span>
            <span>${formatINR(data.grandTotal)}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Tax Summary -->
    ${
      taxSlabs.length > 0
        ? `
    <div class="tax-table">
      <p class="label" style="margin-bottom:6px">Tax Summary (Rate-wise Breakup)</p>
      <table class="border-table" style="width:auto">
        <thead>
          <tr>
            <th>GST Rate</th>
            <th class="right">Taxable Amount</th>
            ${data.isSameState ? '<th class="right">CGST</th><th class="right">SGST</th>' : '<th class="right">IGST</th>'}
          </tr>
        </thead>
        <tbody>
          ${taxSlabs
            .map(
              (s) => `
          <tr>
            <td>${s.rate}%</td>
            <td class="right">${formatINR(s.taxable)}</td>
            ${
              data.isSameState
                ? `<td class="right">${formatINR(s.cgst)}</td><td class="right">${formatINR(s.sgst)}</td>`
                : `<td class="right">${formatINR(s.igst)}</td>`
            }
          </tr>`,
            )
            .join('')}
        </tbody>
      </table>
    </div>`
        : ''
    }

    <!-- Payment Breakdown -->
    ${
      data.paymentModes && data.paymentModes.length > 0
        ? `
    <div style="margin-top:12px;border:1px solid #e2e8f0;border-radius:4px;overflow:hidden">
      <div style="padding:6px 10px;background:#f8fafc;border-bottom:1px solid #e2e8f0;font-size:10px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:0.5px">Payment Breakdown</div>
      ${data.paymentModes
        .map(
          (pm) => `
      <div style="display:flex;justify-content:space-between;padding:5px 10px;border-bottom:1px solid #f1f5f9">
        <span style="color:#64748b">${pm.mode}</span>
        <span style="font-weight:600">${formatINR(pm.amount)}</span>
      </div>`,
        )
        .join('')}
    </div>`
        : ''
    }

    <!-- Notes -->
    ${data.notes ? `<p style="margin-top:10px;font-size:10px;color:#64748b"><strong>Notes:</strong> ${data.notes}</p>` : ''}

    <!-- Signature -->
    <div class="signature">
      <p style="font-weight:600">${c.name}</p>
      <p style="margin-top:4px">Authorised Signatory</p>
    </div>

    <p class="footer-text">This is a computer generated invoice. No signature required if digitally signed.</p>
  </body>
  </html>`;

  const win = window.open('', '_blank');
  if (!win) {
    console.error('⚠️ Could not open print window. Please allow popups.');
    return;
  }
  win.document.write(html);
  win.document.close();
  setTimeout(() => win.print(), 400);
}

// printChallan function
// utils/printDocument.ts - printChallan function with Logo

export async function printChallan(data: PrintChallanData): Promise<void> {
  const c = await getCompany();

  // ✅ Logo handling
  let logoHtml = '';
  if (c.logo_url) {
    const logoUrl = c.logo_url.startsWith('data:image')
      ? c.logo_url
      : `${API_URL}${c.logo_url}`;
    logoHtml = `<img src="${logoUrl}" alt="${c.name}" style="width:50px;height:50px;border-radius:50%;object-fit:cover;border:2px solid #4f46e5;background:#fff;margin-bottom:8px;" />`;
  } else {
    logoHtml = `<div style="width:50px;height:50px;border-radius:50%;background:#e0e7ff;display:flex;align-items:center;justify-content:center;font-size:20px;font-weight:bold;color:#4f46e5;border:2px solid #4f46e5;margin:0 auto 8px auto;">${c.name.charAt(0).toUpperCase()}</div>`;
  }

  const totalAmount = data.items.reduce((s, i) => s + (i.amount || 0), 0);
  const senderAddress = data.warehouseAddress || c.address;
  const senderGstin = data.warehouseGstin || c.gstin;

  const hasSerialOrBatch = data.items.some((i) => {
    const hasBatch = Boolean(i.batchNumber);
    const hasSerials =
      (Array.isArray(i.serials) && i.serials.length > 0) ||
      (Array.isArray(i.serialNumbers) && i.serialNumbers.length > 0) ||
      (typeof i.serialNumbers === 'string' && i.serialNumbers.trim().length > 0);
    return hasBatch || hasSerials;
  });

  const html = `<!DOCTYPE html>
  <html>
  <head>
    <meta charset="utf-8">
    <title>Delivery Challan - ${data.challanNo}</title>
    <style>
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { font-family: Arial, sans-serif; font-size: 11px; color: #1e293b; padding: 10mm; }
      table { width: 100%; border-collapse: collapse; }
      th { background: #f8fafc; font-weight: 600; text-align: left; padding: 6px 8px; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; color: #64748b; border: 1px solid #e2e8f0; }
      td { padding: 5px 8px; border: 1px solid #e2e8f0; }
      .right { text-align: right; }
      .center { text-align: center; }
      .bold { font-weight: 700; }
      .header-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 14px; padding-bottom: 10px; border-bottom: 2px solid #e2e8f0; }
      .doc-title { font-size: 18px; font-weight: 800; color: #4f46e5; text-align: right; }
      .info-box { border: 1px solid #e2e8f0; padding: 8px 12px; border-radius: 4px; display: inline-block; text-align: left; margin-top: 10px; }
      .label { font-size: 9px; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.5px; }
      .address-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 12px; padding: 10px; background: #f8fafc; border-radius: 4px; }
      .footer-text { text-align: center; font-style: italic; color: #94a3b8; font-size: 9px; margin-top: 16px; padding-top: 8px; border-top: 1px solid #f1f5f9; }
      @media print { @page { size: A4; margin: 0; } body { padding: 10mm 12mm; } }
    </style>
  </head>
  <body>
    <div class="header-grid">
      <div style="display:flex;align-items:center;gap:12px;">
        ${logoHtml}
        <div>
          <p style="font-size:16px;font-weight:700;color:#1e293b;">${c.name}</p>
          <p style="color:#64748b;margin-top:4px;line-height:1.5;">${senderAddress}</p>
          <p style="margin-top:4px;">
            <strong>GSTIN:</strong> ${senderGstin} &nbsp;|&nbsp; <strong>PAN:</strong> ${c.pan} &nbsp;|&nbsp; <strong>Ph:</strong> ${c.phone}
          </p>
          <p><strong>Email:</strong> ${c.email}</p>
        </div>
      </div>
      <div style="text-align:right">
        <p class="doc-title">DELIVERY CHALLAN</p>
        <div class="info-box">
          <p class="label">Challan No</p><p class="bold">${data.challanNo}</p>
          <p class="label" style="margin-top:6px">Date</p><p>${data.date}</p>
          ${data.vehicleNo ? `<p class="label" style="margin-top:6px">Vehicle No</p><p>${data.vehicleNo}</p>` : ''}
          ${data.lrNo ? `<p class="label" style="margin-top:6px">LR No</p><p>${data.lrNo}</p>` : ''}
        </div>
      </div>
    </div>

    <div style="display:grid;grid-template-columns:${data.warehouseName ? '1fr 1fr 1fr' : '1fr 1fr'};gap:14px;margin-bottom:12px;padding:10px;background:#f8fafc;border-radius:4px;">
      ${data.warehouseName ? `
      <div>
        <p class="label" style="color:#4f46e5;font-weight:700;">Dispatched From (Warehouse)</p>
        <p class="bold" style="margin-top:3px;font-size:12px">${data.warehouseName}</p>
        ${data.warehouseGstin ? `<p style="margin-top:2px;font-family:monospace;font-size:10px;color:#4f46e5;">GSTIN: ${data.warehouseGstin}</p>` : ''}
        ${data.warehouseAddress ? `<p style="margin-top:2px;color:#64748b;line-height:1.4;">${data.warehouseAddress}</p>` : ''}
      </div>` : ''}
      <div>
        <p class="label">Bill To</p>
        <p class="bold" style="margin-top:3px;font-size:12px">${data.customerName}</p>
        <p style="margin-top:2px;color:#64748b;line-height:1.4;">${data.billingAddress}</p>
      </div>
      <div>
        <p class="label">Ship To</p>
        <p class="bold" style="margin-top:3px;font-size:12px">${data.customerName}</p>
        <p style="margin-top:2px;color:#64748b;line-height:1.4;">${data.shippingAddress || data.billingAddress}</p>
        ${data.driverName ? `
        <div style="margin-top:4px;padding-top:4px;border-top:1px solid #e2e8f0;font-size:9.5px;color:#64748b;">
          Driver: <strong>${data.driverName}</strong> ${data.vehicleNo ? `&nbsp;·&nbsp; Vehicle: <strong>${data.vehicleNo}</strong>` : ''}
        </div>` : ''}
      </div>
    </div>

    <table style="margin-bottom:12px;">
      <thead>
        <tr>
          <th style="width:30px;text-align:center;">Sr</th>
          <th style="text-align:left;">Description</th>
          ${hasSerialOrBatch ? '<th style="text-align:left;">Serial / Batch No</th>' : ''}
          <th style="text-align:right;">Qty</th>
          <th style="text-align:left;">Unit</th>
          ${data.items.some((i) => i.rate) ? '<th style="text-align:right;">Rate</th><th style="text-align:right;">Amount</th>' : ''}
        </tr>
      </thead>
      <tbody>
        ${data.items
          .map(
            (item, i) => {
              let serialList: string[] = [];
              const rawSerials =
                Array.isArray(item.serials) && item.serials.length > 0
                  ? item.serials
                  : Array.isArray(item.serialNumbers) && item.serialNumbers.length > 0
                  ? item.serialNumbers
                  : typeof item.serialNumbers === 'string' && item.serialNumbers.trim()
                  ? item.serialNumbers
                  : [];

              if (Array.isArray(rawSerials)) {
                serialList = rawSerials
                  .map((s: any) =>
                    typeof s === 'string'
                      ? s
                      : s.serialNumber || s.serial_number || s.serialNo || s.id || ''
                  )
                  .filter(Boolean);
              } else if (typeof rawSerials === 'string') {
                serialList = rawSerials.split(/[,\n]+/).map((s: string) => s.trim()).filter(Boolean);
              }

              const parts: string[] = [];
              if (item.batchNumber) {
                parts.push(`Batch: ${item.batchNumber}`);
              }
              if (serialList.length > 0) {
                parts.push(`S/N: ${serialList.join(', ')}`);
              }
              const trackingHtml = parts.length > 0 ? parts.join('<br/>') : '—';

              return `
        <tr style="${i % 2 === 1 ? 'background:#fafafa' : ''}">
          <td class="center">${item.sr}</td>
          <td class="bold">${item.name}</td>
          ${hasSerialOrBatch ? `<td style="font-family:monospace;font-size:10px;color:#4338ca;">${trackingHtml}</td>` : ''}
          <td class="right">${item.qty}</td>
          <td>${item.unit}</td>
          ${
            data.items.some((i2) => i2.rate)
              ? `
          <td class="right">${item.rate ? '₹' + item.rate.toFixed(2) : '—'}</td>
          <td class="right bold">${item.amount ? '₹' + item.amount.toFixed(2) : '—'}</td>`
              : ''
          }
        </tr>`;
            }
          )
          .join('')}
      </tbody>
      ${
        data.items.some((i) => i.amount)
          ? `
      <tfoot>
        <tr style="background:#f8fafc;font-weight:700;">
          <td colspan="${(data.items.some((i) => i.rate) ? 5 : 3) + (hasSerialOrBatch ? 1 : 0)}" style="text-align:right;border:1px solid #e2e8f0;padding:5px 8px;">Total</td>
          <td style="text-align:right;border:1px solid #e2e8f0;padding:5px 8px;">₹${totalAmount.toFixed(2)}</td>
        </tr>
      </tfoot>`
          : ''
      }
    </table>

    <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-top:40px">
      <div style="border-top:1px solid #e2e8f0;padding-top:8px;text-align:center;font-size:10px;color:#64748b">
        <p>Receiver's Signature &amp; Stamp</p>
        <p style="margin-top:4px">Date: ________________</p>
      </div>
      <div style="border-top:1px solid #e2e8f0;padding-top:8px;text-align:center;font-size:10px;color:#64748b">
        <p class="bold" style="font-size:11px">${c.name}</p>
        <p style="margin-top:4px">Authorised Signatory</p>
      </div>
    </div>

    <p class="footer-text">This is a computer generated delivery challan.</p>
  </body>
  </html>`;

  const win = window.open('', '_blank');
  if (!win) return;
  win.document.write(html);
  win.document.close();
  setTimeout(() => win.print(), 400);
}

// printPurchaseOrder function
export async function printPurchaseOrder(data: PrintPOData): Promise<void> {
  const c = await getCompany();

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Purchase Order - ${data.poNo}</title>
  <style>${commonStyles()}</style></head><body>
  <div class="header-grid">
    <div>
      <p class="company-name">${c.name}</p>
      <p style="color:#64748b;margin-top:4px;line-height:1.5">${c.address}</p>
      <p style="margin-top:4px"><strong>GSTIN:</strong> ${c.gstin} &nbsp;|&nbsp; <strong>PAN:</strong> ${c.pan}</p>
      <p><strong>Ph:</strong> ${c.phone} &nbsp;|&nbsp; <strong>Email:</strong> ${c.email}</p>
    </div>
    <div style="text-align:right">
      <p class="doc-title">PURCHASE ORDER</p>
      <div style="margin-top:10px;display:inline-block;border:1px solid #e2e8f0;padding:8px 12px;border-radius:4px;text-align:left">
        <p class="label">PO No</p><p class="bold">${data.poNo}</p>
        <p class="label" style="margin-top:6px">PO Date</p><p>${data.date}</p>
        ${data.deliveryDate ? `<p class="label" style="margin-top:6px">Delivery By</p><p>${data.deliveryDate}</p>` : ''}
        ${data.paymentTerms ? `<p class="label" style="margin-top:6px">Payment Terms</p><p>${data.paymentTerms}</p>` : ''}
      </div>
    </div>
  </div>
  <div class="address-grid">
    <div>
      <p class="label">Vendor (To)</p>
      <p class="bold" style="margin-top:3px;font-size:12px">${data.supplierName}</p>
      ${data.supplierGstin ? `<p style="margin-top:2px">GSTIN: ${data.supplierGstin}</p>` : ''}
      <p style="margin-top:2px;color:#64748b;line-height:1.5">${data.supplierAddress}</p>
    </div>
    <div>
      <p class="label">Ship To (Delivery Address)</p>
      <p class="bold" style="margin-top:3px;font-size:12px">${c.name}</p>
      <p style="margin-top:2px;color:#64748b;line-height:1.5">${c.address}</p>
    </div>
  </div>
  <table class="border-table section">
    <thead><tr>
      <th style="width:30px">Sr</th><th>Item Description</th><th>HSN Code</th>
      <th class="right">Qty</th><th>Unit</th><th class="right">Rate</th><th class="right">Amount</th>
    </tr></thead>
    <tbody>
      ${data.items
        .map(
          (item, i) => `<tr style="${i % 2 === 1 ? 'background:#fafafa' : ''}">
        <td class="center">${item.sr}</td><td class="bold">${item.name}</td>
        <td>${item.hsn}</td><td class="right">${item.qty}</td><td>${item.unit}</td>
        <td class="right">${formatINR(item.rate)}</td><td class="right bold">${formatINR(item.amount)}</td>
      </tr>`,
        )
        .join('')}
    </tbody>
    <tfoot><tr style="background:#f8fafc;font-weight:700">
      <td colspan="6" class="right">Total Amount</td><td class="right">${formatINR(data.totalAmount)}</td>
    </tr></tfoot>
  </table>
  ${data.notes ? `<div style="margin-top:10px;padding:8px 12px;background:#f8fafc;border-left:3px solid #4f46e5"><p class="label">Terms &amp; Conditions</p><p style="margin-top:4px">${data.notes}</p></div>` : ''}
  <div style="margin-top:10px;padding:8px 12px;background:#fffbeb;border:1px solid #fde68a;border-radius:4px">
    <p class="label">Important Notes</p>
    <p style="margin-top:4px">1. Please acknowledge receipt of this PO within 24 hours. 2. Mention PO number on all invoices and delivery documents. 3. Goods must match specifications. 4. No partial delivery without prior approval.</p>
  </div>
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-top:40px">
    <div style="border-top:1px solid #e2e8f0;padding-top:8px;text-align:center;font-size:10px;color:#64748b">
      <p>Vendor's Acceptance</p>
      <p style="margin-top:4px">Date: ________________</p>
    </div>
    <div style="border-top:1px solid #e2e8f0;padding-top:8px;text-align:center;font-size:10px;color:#64748b">
      <p class="bold" style="font-size:11px">${c.name}</p>
      <p style="margin-top:4px">Authorised Signatory (Purchase)</p>
    </div>
  </div>
  <p class="footer-text">This is a computer generated purchase order.</p>
  </body></html>`;

  const win = window.open('', '_blank');
  if (!win) return;
  win.document.write(html);
  win.document.close();
  setTimeout(() => win.print(), 400);
}
